// Route `api` cho Thanh toán (N-DT): `/v1/finance/payments` (bảng Payment, phiếu thu) + `/v1/finance/supplierpay` (TblSupplierPayment, phiếu chi) → `PayTx`.
// Cả hai route nằm sau quyền money (worker trả 403 → DataError 'noperm'). Không lấy số tài khoản ngân hàng.
// Dữ liệu đọc chưa có phân bổ khoản (al) / khớp sao kê: phiếu đã duyệt coi là khớp đủ, chưa duyệt là chưa khớp.
import type { ApiRoute } from '@hana/data';
import type { AccKey, PayTx } from './data';

type Row = Record<string, unknown>;
const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const num = (v: unknown): number => { const n = typeof v === 'string' ? Number(v) : v; return typeof n === 'number' && Number.isFinite(n) ? n : 0; };
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const p2 = (n: number) => String(n).padStart(2, '0');

/** ISO → '22/Oct' (khớp dayKey của data.ts); giờ lấy từ `at` (HH:mm). Giờ địa phương của trình duyệt. */
export function dayOf(iso: unknown): { d: string; at: string } {
  const dt = new Date(str(iso));
  if (Number.isNaN(dt.getTime())) return { d: '', at: '' };
  return { d: `${p2(dt.getDate())}/${MON[dt.getMonth()]}`, at: `${p2(dt.getHours())}:${p2(dt.getMinutes())}` };
}

export function accOf(account: string): AccKey {
  const a = account.toUpperCase();
  if (a.includes('USD')) return 'TPU';
  if (a.includes('TCB') || a.includes('TECHCOM')) return 'TCB';
  if (a.includes('CASH') || a.includes('TIỀN MẶT') || a.includes('TIEN MAT')) return 'CASH';
  return 'TP';
}

/** VND → triệu (đơn vị của màn). */
const tr = (v: unknown): number => Math.round(num(v) / 1e5) / 10;

export function receiptFromApi(r: Row): PayTx {
  const ref = str(r.REF), ok = r.Approved === true;
  return { id: ref, tab: 'thu', ...dayOf(r.DATEINPUT ?? r.DATE), acc: accOf(str(r.Account)), p: str(r.ShortName), amt: tr(r.TOTALREVENUE), cur: 'VND', doc: ['PT', ref, 0], al: [], m: ok ? 'ok' : 'none', s: ok ? 'post' : 'rec', by: str(r.USER) };
}

export function payoutFromApi(r: Row): PayTx {
  const ref = str(r.REF), ok = r.Approved === true;
  return { id: ref, tab: 'chi', ...dayOf(r.DATEINPUT ?? r.DATEPAYNCC), acc: accOf(str(r.Account)), p: str(r.ShortName), amt: tr(r.AMOUNT), cur: 'VND', doc: ['UNC', ref, 0], al: [], m: ok ? 'ok' : 'none', s: ok ? 'post' : 'rec', by: str(r.USER) };
}

const items = (json: unknown): Row[] => {
  const list = (json as { items?: unknown } | null)?.items;
  return Array.isArray(list) ? (list as Row[]) : [];
};
const route = (path: string, map: (r: Row) => PayTx): ApiRoute<PayTx> => ({
  path,
  query: (q) => {
    const limit = q.limit && q.limit > 0 ? q.limit : 50;
    return { limit, page: Math.floor((q.offset ?? 0) / limit) + 1 };
  },
  parse: (json) => ({ items: items(json).map(map), hasMore: (json as { hasMore?: boolean } | null)?.hasMore === true }),
});

export const payRecvApiRoute = route('/api/v1/finance/payments', receiptFromApi);
export const payOutApiRoute = route('/api/v1/finance/supplierpay', payoutFromApi);
